package com.ijoin.data.api

import com.ijoin.data.api.ApiClient.addAuthToken
import com.ijoin.data.model.Booking
import com.ijoin.data.model.CreateBookingRequest
import com.ijoin.data.model.EventBookingResult
import com.ijoin.data.model.EventTicketBookingRequest
import com.ijoin.data.model.Ticket
import com.ijoin.data.model.TimeSlot
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.contentType
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.serialization.Serializable
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json

class BookingApi {
    private val client = ApiClient.client
    private val json = Json {
        ignoreUnknownKeys = true
        isLenient = true
    }

    suspend fun getServiceProviderBookings(serviceProviderId: String): Result<List<Booking>> {
        return safeApiCall {
            client.get("${ApiClient.getBaseUrl()}/api/bookings/provider/$serviceProviderId") {
                addAuthToken()
            }
        }.mapCatching { response ->
            response.body()
        }
    }

    suspend fun getAvailableSlots(
        eventId: String,
        startDate: LocalDate,
        endDate: LocalDate,
    ): Result<List<TimeSlot>> {
        return safeApiCall {
            client.get("${ApiClient.getBaseUrl()}/api/service-providers/$eventId/available-slots") {
                addAuthToken()
                parameter("startDate", startDate.toString())
                parameter("endDate", endDate.toString())
            }
        }.mapCatching { response ->
            response.body()
        }
    }

    suspend fun createBooking(request: CreateBookingRequest): Result<Booking> {
        return safeApiCall {
            client.post("${ApiClient.getBaseUrl()}/api/bookings") {
                addAuthToken()
                contentType(ContentType.Application.Json)
                setBody(request)
            }
        }.mapCatching { response ->
            response.body()
        }
    }

    suspend fun bookEventTickets(request: EventTicketBookingRequest): Result<EventBookingResult> {
        return safeApiCall {
            client.post("${ApiClient.getBaseUrl()}/api/bookings/events") {
                addAuthToken()
                contentType(ContentType.Application.Json)
                setBody(request)
            }
        }.mapCatching { response ->
            val payload = response.bodyAsText()
            val booking = runCatching {
                json.decodeFromString<Booking>(payload)
            }.getOrNull()
            if (booking != null) {
                EventBookingResult.Reservation(booking)
            } else {
                val tickets = json.decodeFromString<List<Ticket>>(payload)
                EventBookingResult.TicketsIssued(tickets)
            }
        }
    }

    suspend fun pingReservation(bookingId: Long): Result<Unit> {
        return safeApiCall {
            client.post("${ApiClient.getBaseUrl()}/api/bookings/$bookingId/ping") {
                addAuthToken()
            }
        }.map { }
    }

    /**
     * Get enriched bookings for an event (organizer only).
     * Returns bookings with tickets, contact requests, and payments.
     */
    suspend fun getEnrichedBookings(
        eventId: String,
        from: Instant? = null,
        to: Instant? = null,
    ): Result<OrganizerBookingsResponse> {
        return safeApiCall {
            client.get("${ApiClient.getBaseUrl()}/api/bookings/event/$eventId/enriched") {
                addAuthToken()
                from?.let { parameter("from", it.toString()) }
                to?.let { parameter("to", it.toString()) }
            }
        }.mapCatching { response ->
            response.body()
        }
    }

    // Response DTOs for enriched bookings

    @Serializable
    data class OrganizerBookingsResponse(
        val enrichedBookings: List<EnrichedBookingDto> = emptyList(),
        val unconfirmedRequests: List<ContactRequestDto> = emptyList(),
    )

    @Serializable
    data class EnrichedBookingDto(
        val booking: Booking,
        val tickets: List<Ticket> = emptyList(),
        val contactRequest: ContactRequestDto? = null,
        val payment: PaymentDto? = null,
    )

    @Serializable
    data class ContactRequestDto(
        val id: Long,
        val eventId: String,
        val userId: Long? = null,
        val phone: String? = null,
        val comment: String? = null,
        val bookingId: Long? = null,
        val formData: kotlinx.serialization.json.JsonObject? = null,
        val createdAt: String? = null,
    )

    @Serializable
    data class PaymentDto(
        val id: String,
        val userId: String? = null,
        val eventId: String? = null,
        val amount: Double? = null,
        val currency: String? = null,
        val ticketQuantity: Int? = null,
        val priceTierName: String? = null,
        val paymentMethod: String? = null,
        val transactionId: String? = null,
        val status: String? = null,
        val receiptUrl: String? = null,
        val createdAt: String? = null,
        val completedAt: String? = null,
    )
}
